import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams, useLocation, useOutletContext } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Divider,
  darken,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  FormControl,
} from "@mui/material";
import { lighten } from "@material-ui/core";
import CopiedTooltip from "../components/common/CopiedTooltip";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

import Cookies from "js-cookie";
import { selectCurrentToken } from "../features/slices/authSlice";

import Image from "../components/common/Image";
import Announcement from "../components/common/Announcement";
import ImageCarousel from "../components/common/ImageCarousel";
import PopupBanner, { shouldShowPopupBanner } from "../components/common/PopupBanner";
import LoginReward from "../components/LoginReward";
import TextCarousel from "../components/common/TextCarousel";
import Backdrop from "../components/common/Backdrop";
import Swal from "sweetalert2";

import {
  useGetGameCategoryQuery,
  useLazyGetGameProviderQuery,
  useGetGameProviderQuery,
  useLazyGetLaunchGameQuery,
  useLazyGetGameItemQuery
} from "../features/api/gameApiSlice";
import { useGetHomePageQuery, useGetPopupBannerQuery } from "../features/api/homeApiSlice";
import { useGetLiveMatchesQuery, useLazyGetRegisterPopupQuery, useGetWhatsappGroupQuery } from "../features/api/publicApiSlice";
import { showRegisterSuccessPopup } from "../utils/registerPopup";
import { useGetWithdrawDetailsQuery } from "../features/api/transactionApiSlice";
import { useGetShareLinkQuery } from "../features/api/userApiSlice";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import GameCard from "../components/Game/GameCardV2";
import * as Constant from "../features/constant";
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import LaunchDemo from "../components/common/LaunchDemo";
import SearchIcon from '@mui/icons-material/Search';

const pattern = /^((http|https|ftp):\/\/)/;

// Promo button background colors (can be extended via env vars)
const BUTTON_COLORS = {
  Referral: process.env.REACT_APP_PROMO_BTN_COLOR_REFERRAL || '#0b7051',
  App: process.env.REACT_APP_PROMO_BTN_COLOR_APP || '#4c6ceb',
  Deposit: process.env.REACT_APP_PROMO_BTN_COLOR_DEPOSIT || '#c11245',
  Withdrawal: process.env.REACT_APP_PROMO_BTN_COLOR_WITHDRAWAL || '#af8c65',
  Vip: process.env.REACT_APP_PROMO_BTN_COLOR_REFERRAL || '#0b7051',
  WhatsappGroup: process.env.REACT_APP_PROMO_BTN_COLOR_WHATSAPP || '#f57c00',
};

// Slot and fish hunter use portrait thumbnails → 3 cols. Live/sports use landscape → 2 cols.
const getGridCols = (category) => {
  if (!category || category === 'HOT_GAME') return 3;
  if (!category || category === 'LIVE_CASINO' || category === 'SPORTBOOK' || category === 'CRICKET' || category === 'ESPORTS' || category === 'COCKFIGHT') return 1;
  return 2;
};

const Home_v2 = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [popupOpen, setPopupOpen] = useState(
    shouldShowPopupBanner
  );
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletView = useMediaQuery(theme.breakpoints.down("md"));
  const isLoggedIn = useSelector(selectCurrentToken);
  const [number, setNumber] = useState(14020000);
  const lang = localStorage.getItem("language");

  const [filteredLiveMatches, setFilteredLiveMatches] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const sliderRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const [currentProviderId, setCurrentProviderId] = useState(null);
  const [offset, setOffset] = useState(1);
  const [appGameDisplay, setAppGameDisplay] = useState(false);
  const [appProviderName, setAppProviderName] = useState("");
  const [showLoginReward, setShowLoginReward] = useState(false);
  const location = useLocation();
  const useSEO = process.env.REACT_APP_SEO === "TRUE";
  const homeGlowUi = process.env.REACT_APP_HOME_GLOW_UI === "true";

  // Mobile All Provider section state
  const [mobileProviderCategory, setMobileProviderCategory] = useState(null);
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const allProviderRef = useRef(null);
  const scrollToProviderFlag = useRef(false);
  const isMountComplete = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => { isMountComplete.current = true; }, 0);
    return () => clearTimeout(t);
  }, []);

  // When navigating to home from another page via drawer category click,
  // location.state.scrollToCategory is set — trigger scroll without isMountComplete guard
  useEffect(() => {
    const cat = location.state?.scrollToCategory;
    if (cat && mobileView) {
      setMobileProviderCategory(cat);
      scrollToProviderFlag.current = true;
      // Clear the state so back-navigation doesn't re-trigger the scroll
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const [triggerGetRegisterPopup] = useLazyGetRegisterPopupQuery();

  // Config-backed link for the "Whatsapp Community Group" home card, also
  // injected into the register popup's Join Now button (see effect below).
  const { data: whatsappGroup } = useGetWhatsappGroupQuery();
  const whatsappGroupUrl = whatsappGroup?.result?.value;

  // After registration + auto-login, Register/RegisterModal navigate here with
  // location.state.justRegistered set — show the backend-driven popup once landed on home.
  // RegisterModal overlays this page without unmounting it, so this can't be mount-only —
  // it has to react to location.state changing after the initial mount too. Also wait for
  // whatsappGroupUrl so the popup's Join Now button never gets built with an empty link.
  useEffect(() => {
    if (location.state?.justRegistered && whatsappGroupUrl) {
      showRegisterSuccessPopup(triggerGetRegisterPopup, { whatsappUrl: whatsappGroupUrl });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, whatsappGroupUrl]);

  const {
    selectedGameCategory,
    setSelectedGameCategory,
    depositDetails
  } = useOutletContext();

  // APK download helpers (replicated from WalletCard)
  const apkUrl = `${window.location.origin}/${process.env.REACT_APP_APK}`;
  const { data: withdrawDetails } = useGetWithdrawDetailsQuery();

  const openAppOrDownload = () => {
    const appLink = process.env.REACT_APP_APK_SCHEME;
    const landingUrl = process.env.REACT_APP_APK_LANDING_URL;

    if (landingUrl) {
      window.open(landingUrl, "_blank");
    } else {
      let appOpened = false;
      const handleVisibilityChange = () => {
        if (document.hidden) appOpened = true;
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      const iframe = document.createElement("iframe");
      iframe.src = appLink;
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      setTimeout(() => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.body.removeChild(iframe);
        if (!appOpened) window.location.href = apkUrl;
      }, 1500);
    };
  }

  const handleWalletCardClick = (screen) => {
    // Joining the chat group needs no account, same as app/referral.
    const noTokenRequired = ["referral", "app", "whatsappgroup"].includes(screen?.code?.toLowerCase());
    if (!isLoggedIn && !noTokenRequired) {
      navigate({ pathname: location.pathname, search: "?login=1" });
      return;
    }
    switch (screen?.code?.toLowerCase()) {
      case "app":
        openAppOrDownload();
        break;
      case "whatsappgroup": {
        // if value is "livechat" opens live chat, empty/null does nothing, anything else opens it as a URL.
        const value = whatsappGroupUrl?.trim();
        if (value?.toLowerCase() === "livechat") {
          if (
            typeof window.LiveChatWidget !== "undefined" &&
            window.LiveChatWidget.call
          ) {
            window.LiveChatWidget.call("maximize");
          }
        } else if (value) {
          window.open(value, "_blank", "noopener,noreferrer");
        }
        break;
      }
      case "referral":
        if (process.env.REACT_APP_FOOTER_REPLACE_AFFILIATE_WITH_REFERRAL === "true") {
          if (isLoggedIn) {
            navigate(Constant.getPath("/referral"));
          } else {
            navigate({ pathname: location.pathname, search: "?login=1" });
          }
        } else {
          navigate(Constant.getPath("/affiliate"), { state: { screen: "Affiliate" } });
        }
        break;
      case "deposit":
        navigate(Constant.getPath("/deposit"), { state: { depositDetails } });
        break;
      case "withdrawal":
        navigate(Constant.getPath("/withdrawal"), { state: { withdrawDetails: withdrawDetails?.result } });
        break;
      default:
        navigate(Constant.getPath("/" + screen?.code?.toLowerCase()));
    }
  };

  const handleCopyReferralLink = () => {
    if (!isLoggedIn) {
      navigate({ pathname: location.pathname, search: "?login=1" });
      return;
    }
    const url = shareLink?.result?.url;
    if (url) {
      navigator.clipboard.writeText(url).catch(() => { });
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    }
  };

  // Game launch helpers
  const handleHotGameClick = (game) => {
    if (!isLoggedIn) {
      if (game?.demoSupported) {
        playDemo(game);
      } else {
        navigate({ pathname: location.pathname, search: "?login=1" });
      }
    } else {
      getLaunchGame({ id: game?.id, type: game?.gameType, category: game.categoryCode, isMobile: mobileView });
    }
  };

  const handleProviderClick = (provider) => {
    const hasGameList = gameCategory?.result?.find(
      (category) => category.code === mobileProviderCategory
    )?.hasGameList;

    if (hasGameList) {
      navigate(
        Constant.getPath(`/game/${mobileProviderCategory?.toLowerCase()}`),
        { state: { providerId: provider.id } }
      );
    } else {
      if (!isLoggedIn) {
        if (provider.demoSupported) {
          playDemoProvider(provider, mobileProviderCategory);
        } else {
          navigate({ pathname: location.pathname, search: "?login=1" });
        }
      } else {
        setAppProviderName(provider?.name);
        getLaunchGame({ id: provider?.id, type: provider?.gameType, category: mobileProviderCategory, isMobile: mobileView });
      }
    }
  };

  const walletCardScreens = [
    {
      label: t("walletcard.appdownload"),
      code: "App",
      subtitle: t("walletcard.app.subtitle") !== "walletcard.app.subtitle"
        ? t("walletcard.app.subtitle") : "Download & play",
      icon: (
        <Image width="35px" height="35px" src={process.env.REACT_APP_HOME_APP_DOWNLOAD} alt={Constant.ALT_TAG_ICON_APP_DOWNLOAD} />
      ),
    },
    {
      label: typeof t("walletcard.whatsappgroup") === "string" && t("walletcard.whatsappgroup") !== "walletcard.whatsappgroup"
        ? t("walletcard.whatsappgroup") : "Whatsapp",
      code: "WhatsappGroup",
      subtitle: t("walletcard.whatsappgroupsubtitle") !== "walletcard.whatsappgroupsubtitle"
        ? t("walletcard.whatsappgroupsubtitle") : "Get Code to Redeem",
      icon: (
        <Image width="35px" height="35px" src={process.env.REACT_APP_HOME_WHATSAPP || process.env.PUBLIC_URL + "/assets/img/whatsappgroup.png"} alt="Whatsapp Community Group" />
      ),
    },
    {
      label: typeof t("walletcard.deposit") === "string" && t("walletcard.deposit") !== "walletcard.deposit"
        ? t("walletcard.deposit") : "Deposit",
      code: "Deposit",
      subtitle: t("walletcard.depositsubtitle") !== "walletcard.depositsubtitle"
        ? t("walletcard.depositsubtitle") : "Fast & secure",
      icon: (
        <Image width="35px" height="35px" src={process.env.REACT_APP_HOME_DEPOSIT} alt={Constant.ALT_TAG_ICON_DEPOSIT} />
      ),
    },
    {
      label: typeof t("walletcard.withdrawal") === "string" && t("walletcard.withdrawal") !== "walletcard.withdrawal"
        ? t("walletcard.withdrawal") : "Withdrawal",
      code: "Withdrawal",
      subtitle: t("walletcard.withdrawalsubtitle") !== "walletcard.withdrawalsubtitle"
        ? t("walletcard.withdrawalsubtitle") : "Instant payout",
      icon: (
        <Image width="35px" height="35px" src={process.env.REACT_APP_HOME_WITHDRAW} alt={Constant.ALT_TAG_ICON_WITHDRAW} />
      ),
    },
    {
      label: typeof t("walletcard.vip") === "string" && t("walletcard.vip") !== "walletcard.vip"
        ? t("walletcard.vip") : "VIP",
      code: "Vip",
      subtitle: t("walletcard.vipsubtitle") !== "walletcard.vipsubtitle"
        ? t("walletcard.vipsubtitle") : "Exclusive rewards",
      icon: (
        <Image width="35px" height="35px" src={process.env.REACT_APP_HOME_VIP} alt={Constant.ALT_TAG_ICON_VIP} />
      )
    },
  ];

  const homePageSeo = t("home.seo.content") !== "home.seo.content" && t("home.seo.title") !== "home.seo.title" ? (
    <>
      <Typography fontWeight={800} color="white"><h1>{i18n.t("home.seo.title")}</h1></Typography>
      <Typography mt={2} color="white" dangerouslySetInnerHTML={{ __html: t("home.seo.content") }}></Typography>
    </>
  ) : null;

  const {
    data: gameCategory,
    isFetching: isGameCategoryFetching,
    isLoading: isGameCategoryLoading,
    isError: isGameCategoryError,
    isSuccess: isGameCategorySuccess,
    error: gameCategoryError,
  } = useGetGameCategoryQuery({ prefetch: true });

  const [additionalGameBarItems, setAdditionalGameBarItems] = useState([]);

  useEffect(() => {
    // set REACT_APP_HIDE_HOT_GAME=true to drop the Hot Game tab.
    const showHotGame = process.env.REACT_APP_HIDE_HOT_GAME !== 'true';
    const customMenuItems = (showHotGame
      ? [{ name: 'Hot Game', code: 'HOT_GAME', image: process.env.REACT_APP_HEADER_LOGO + '/HOT_GAME.png', imageSelected: process.env.REACT_APP_HEADER_LOGO + '/HOT_GAME.gif' }]
      : []
    ).concat(Constant.getCustomMenuItemsV2(gameCategory?.result, t));
    setAdditionalGameBarItems(customMenuItems);
  }, [gameCategory]);

  // Initialize mobile provider category once menu items are ready
  useEffect(() => {
    if (mobileView && additionalGameBarItems.length > 0 && !mobileProviderCategory) {
      const hasHotGame = additionalGameBarItems.some((item) => item.code === "HOT_GAME");
      setMobileProviderCategory(hasHotGame ? 'HOT_GAME' : additionalGameBarItems[0]?.code);
    }
  }, [mobileView, additionalGameBarItems]);

  const [
    getGameProvider,
    {
      data: gameProvider,
      isFetching: isGameProviderFetching,
      isLoading: isGameProviderLoading,
      isError: isGameProviderError,
      isSuccess: isGameProviderSuccess,
      error: gameProviderError,
    },
  ] = useLazyGetGameProviderQuery({ prefetch: true });

  const {
    data: liveMatches,
    isFetching: isLiveMatchesFetching,
    isLoading: isLiveMatchesLoading,
    isError: isLiveMatchesError,
    error: liveMatchesError,
  } = useGetLiveMatchesQuery();

  useEffect(() => {
    if (liveMatches?.result?.length) {
      try {
        const firstMatch = liveMatches.result[0];
        const category = JSON.parse(firstMatch.category);
        const currentLang = lang ? lang.toLocaleUpperCase() : 'EN';
        if (category[currentLang]) {
          setSelectedCategory(category[currentLang]);
        } else {
          setSelectedCategory(category['EN']);
        }
      } catch (e) {
        console.error("Error initializing category", e);
      }
    }
  }, [liveMatches, lang]);

  useEffect(() => {
    if (liveMatches?.result && selectedCategory) {
      const filtered = liveMatches.result.filter((match) => {
        try {
          const categoryObj = JSON.parse(match.category);
          const matchCategoryName = categoryObj[lang.toLocaleUpperCase()] || categoryObj['EN'];
          return matchCategoryName === selectedCategory;
        } catch (e) {
          console.error("Error parsing match category", e);
          return false;
        }
      });
      setFilteredLiveMatches(filtered);
    } else {
      setFilteredLiveMatches([]);
    }
    setTimeout(() => { sliderRef.current?.slickGoTo(0); }, 0);
  }, [selectedCategory, liveMatches, lang]);

  const slickSettings = {
    dots: true,
    infinite: filteredLiveMatches?.length > 1,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: false,
    afterChange: (current) => setActiveSlide(current),
  };

  const {
    data: allGameProvider,
    isFetching: isAllGameProviderFetching,
    isLoading: isAllGameProviderLoading,
    isError: isAllGameProviderError,
    isSuccess: isAllGameProviderSuccess,
    error: allGameProviderError,
  } = useGetGameProviderQuery({ category: "" }, { prefetch: true });

  const [
    getLaunchGame,
    {
      data: launchGame,
      isFetching: isLaunchGameFetching,
      isLoading: isLaunchGameLoading,
      isError: isLaunchGameError,
      isSuccess: isLaunchGameSuccess,
      error: launchGameError,
    },
  ] = useLazyGetLaunchGameQuery({ prefetch: true });

  const {
    data: homePage,
    isFetching: isHomePageFetching,
    isLoading: isHomePageLoading,
    isError: isHomePagetError,
    isSuccess: isHomePageSuccess,
    error: homePageError,
  } = useGetHomePageQuery({ prefetch: true });

  const { data: shareLink } = useGetShareLinkQuery(undefined, { skip: !isLoggedIn });

  const featureGamesByCategory = useMemo(() => {
    const games = homePage?.result?.featureGames || [];
    return games.reduce((acc, game) => {
      const code = game.categoryCode;
      if (!acc[code]) acc[code] = [];
      acc[code].push(game);
      return acc;
    }, {});
  }, [homePage?.result?.featureGames]);

  const {
    data: popupBanner,
    isFetching: isPopupBannerFetching,
    isLoading: isPopupBannerLoading,
    isError: isPopupBannerError,
    isSuccess: isPopupBannerSuccess,
    error: PopupBannerError,
  } = useGetPopupBannerQuery({ prefetch: true });

  const [
    getGameItem,
    {
      data: gameItem,
      isFetching: isGameItemFetching,
      isLoading: isGameItemLoading,
      isError: isGameItemError,
      isSuccess: isGameItemSuccess,
      error: gameItemError,
    },
  ] = useLazyGetGameItemQuery({ prefetch: true });

  const handlePageChange = (e, p) => {
    setOffset(p);
    window.scroll(0, 0);
  };

  useEffect(() => {
    if (!isLaunchGameLoading && !isLaunchGameFetching && isLaunchGameSuccess) {
      if (launchGame?.result?.gameType === "APP") {
        setAppGameDisplay(true);
      } else if (launchGame?.result?.gameType === "WEB") {
        if (launchGame?.result?.iframeSupported == true) {
          sessionStorage.setItem("prevPageBeforeGame", window.location.href);
          window.parent.location.href =
            Constant.getPath("/gameLauncher") + "?url=" + launchGame.result.gameUrl;
        } else {
          navigate(Constant.getPath("/gameRedirect") + "?url=" + launchGame.result.gameUrl);
        }
      }
    }
  }, [isLaunchGameLoading, isLaunchGameFetching, isLaunchGameSuccess]);

  useEffect(() => {
    if (isGameItemError) {
      Swal.fire({ text: gameItemError?.data?.message, icon: "error" });
    }
  }, [isGameItemError]);

  useEffect(() => {
    if (isLaunchGameError) {
      Swal.fire({ text: launchGameError?.data?.message, icon: "error" });
    }
  }, [isLaunchGameError]);

  const add = () => setNumber((number) => number + 1);

  useEffect(() => {
    const interval = setInterval(() => { add(); }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Desktop: always fetch SLOT providers for the provider icon bar
  useEffect(() => {
    if (!mobileView) {
      getGameProvider({ category: "SLOT" });
    }
  }, [gameCategory, mobileView]);

  // Mobile All Provider: fetch providers when category changes (skip HOT_GAME - uses recommendGame)
  useEffect(() => {
    if (mobileView && mobileProviderCategory && mobileProviderCategory !== 'HOT_GAME') {
      getGameProvider({ category: mobileProviderCategory });
    }
  }, [mobileProviderCategory, mobileView]);

  // Keep outlet context selectedGameCategory in sync for routing (desktop path)
  useEffect(() => {
    if (!mobileView && !selectedGameCategory) {
      const hasHotGame = additionalGameBarItems.some((item) => item.code === "HOT_GAME");
      setSelectedGameCategory(hasHotGame ? "HOT_GAME" : additionalGameBarItems[0]?.code);
    }
  }, [mobileView, additionalGameBarItems]);

  // Sync drawer category selection → mobile UI, then scroll to provider section
  useEffect(() => {
    if (mobileView && selectedGameCategory) {
      setMobileProviderCategory(selectedGameCategory);
      if (isMountComplete.current) {
        scrollToProviderFlag.current = true;
      }
    }
  }, [selectedGameCategory, mobileView]);

  useEffect(() => {
    if (scrollToProviderFlag.current && allProviderRef.current) {
      scrollToProviderFlag.current = false;
      setTimeout(() => allProviderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [mobileProviderCategory]);

  useEffect(() => {
    if (searchParams.get("refcode")) {
      localStorage.setItem("refCode", searchParams.get("refcode"));
      navigate({ pathname: location.pathname, search: "?register=1" });
    }
  }, []);

  useEffect(() => {
    setAppGameDisplay(false);
  }, [selectedGameCategory, mobileProviderCategory]);

  useEffect(() => {
    const searchParamsObj = new URLSearchParams(window.location.search);
    const clickid = searchParamsObj.get("clickid");
    if (clickid) {
      Cookies.set("clickid", clickid, { expires: 7, path: "/" });
    }
  }, []);

  // Desktop game items (matches original Home.js exactly)
  useEffect(() => {
    if (!mobileView) {
      setAppGameDisplay(false);
      getGameItem({
        category: "SLOT",
        offset: offset - 1,
        providerId: currentProviderId,
        keyword: "",
        group: currentProviderId == null ? "" : "ALL",
        hasProvider: currentProviderId != null,
      });
    }
  }, [offset, currentProviderId, mobileView]);

  useEffect(() => {
    setOffset(1);
  }, [currentProviderId]);

  const closePopup = () => setPopupOpen(false);

  const { playDemo, playDemoProvider, dialog } = LaunchDemo();

  useEffect(() => {
    if (isLoggedIn) {
      const hasShownReward = sessionStorage.getItem('hasShownLoginReward');
      if (!hasShownReward) {
        setShowLoginReward(true);
        sessionStorage.setItem('hasShownLoginReward', 'true');
      }
    } else {
      sessionStorage.removeItem('hasShownLoginReward');
      setShowLoginReward(false);
    }
  }, [isLoggedIn]);

  if (
    isHomePageFetching || isHomePageLoading ||
    isAllGameProviderFetching || isAllGameProviderLoading ||
    isGameCategoryFetching || isGameCategoryLoading
  ) {
    return <Backdrop />;
  }

  if (isLaunchGameFetching || isLaunchGameLoading) {
    return <Backdrop />;
  }

  // ─── Live Matches Display Helpers ──────────────────────────────────────────

  const liveMatchesDisplay = (
    <Box
      paddingX={mobileView ? 2 : undefined}
      width="100%"
      display="flex"
      flexDirection="row"
      color="black"
      alignSelf="center"
      justifyContent="space-between"
    >
      <Typography
        width={mobileView ? "20%" : "100px"}
        color="white"
        fontSize={mobileView ? "12px" : "14px"}
        fontWeight="bold"
      >
        {String(filteredLiveMatches?.[activeSlide ?? 0]?.team1 || "")}
      </Typography>

      <Box paddingTop={4} textAlign="center">
        <Typography color="white" fontWeight="bold" fontSize={mobileView ? "10px" : "18px"}>
          {filteredLiveMatches?.[activeSlide ?? 0]?.gameDate}
        </Typography>
        <Typography color="white" fontSize={mobileView ? "9px" : "14px"}>
          {filteredLiveMatches?.[activeSlide ?? 0]?.gameTime}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          sx={{
            width: mobileView ? "70px" : "80px",
            height: mobileView ? "25px" : "30px",
            fontWeight: "bold",
            fontSize: mobileView ? "8px" : "10px",
            alignSelf: "center",
          }}
          onClick={() => navigate(Constant.getPath("/game/sportbook"))}
        >
          {t("home.previewbutton")}
        </Button>
      </Box>

      <Typography
        width={mobileView ? "20%" : "100px"}
        color="white"
        fontSize={mobileView ? "12px" : "14px"}
        fontWeight="bold"
      >
        {String(filteredLiveMatches?.[activeSlide ?? 0]?.team2 || "")}
      </Typography>
    </Box>
  );

  const liveMatchesImages = (match) => (
    <Box
      display="flex"
      alignItems="center"
      paddingX={mobileView ? "15px" : "0px"}
      justifyContent="space-between"
    >
      <Image width={mobileView ? 65 : 100} height={mobileView ? 65 : 100} src={match.team1Logo} />
      <Image width={mobileView ? 65 : 100} height={mobileView ? 65 : 100} src={match.team2Logo} />
    </Box>
  );

  // ─── Reusable: Glow Divider (JSX variable, not a component) ──────────────────

  const glowDivider = homeGlowUi ? (
    <Divider
      sx={{
        height: 1,
        background: 'none',
        position: 'relative',
        overflow: 'visible',
        '&::before': {
          content: '""',
          display: 'block',
          width: '100%',
          height: '2px',
          background: `linear-gradient(to right, rgba(212,175,55,0.6) 0%, ${process.env.REACT_APP_COLOR_DIVIDER1 ?? theme.palette.secondary.main} 50%, rgba(212,175,55,0.6) 100%)`,
          boxShadow: `0 0 6px ${process.env.REACT_APP_COLOR_DIVIDER1 ?? theme.palette.secondary.main}`,
        },
      }}
    />
  ) : (
    <Divider sx={{ backgroundColor: process.env.REACT_APP_COLOR_DIVIDER1 || theme.palette.secondary.main, width: '100%' }} />
  );

  // ─── Mobile: Section Header (render function, called as function not component) ──

  const renderSectionHeader = (icon, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 1, mb: 0.75, mt: 1 }}>
      {icon && (
        <Box component="img" src={icon} sx={{ width: 18, height: 18, mr: 0.75, objectFit: 'contain' }} />
      )}
      <Typography sx={{
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.secondary.main,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </Typography>
    </Box>
  );

  const renderSectionHeaderFeatured = (icon, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
      {icon && (
        <Box component="img" src={icon} sx={{ width: 40, height: 40, mr: 0.75, objectFit: 'contain' }} />
      )}
      <Typography sx={{
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.secondary.main,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </Typography>
    </Box>
  );

  // ─── Mobile: Horizontal Game Scroller (render function) ──────────────────────

  const renderHorizontalGameScroller = (games, onGameClick) => (
    <Box sx={{
      display: 'flex',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      gap: 1,
      px: 1,
      pb: 0.5,
    }}>
      {games.map((game, idx) => (
        <Box
          key={idx}
          onClick={() => onGameClick(game)}
          sx={{ flexShrink: 0, width: 120, cursor: 'pointer' }}
        >
          <Image
            src={game.homeThumbnailUrl}
            alt={Constant.ALT_TAG_GENERAL_GAME + game.name}
            sx={{ width: '100%', borderRadius: '8px', display: 'block' }}
          />
          <Typography noWrap sx={{
            fontSize: 9,
            color: process.env.REACT_APP_COLOR_TEXT || 'white',
            mt: 0.5,
          }}>
            {game.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // ─── Mobile: Promo Button Row ───────────────────────────────────────────────

  const mobilePromoButtons = (
    <Box sx={{ overflowX: 'hidden', py: 1 }}>
      <Box sx={{
        display: 'flex',
        gap: 1.25,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        pb: 0.5,
        px: 1.25,
      }}>
        {walletCardScreens
          .filter(screen => !(screen.code === 'App' && process.env.REACT_APP_WALLETCARD_SHOW_APP_DOWNLOAD === 'false'))
          .map((screen) => (
            <Box
              key={screen.code}
              onClick={() => handleWalletCardClick(screen)}
              sx={{
                minWidth: 148,
                maxWidth: 180,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: '12px',
                background: BUTTON_COLORS[screen.code],
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                cursor: 'pointer',
                flexShrink: 0,
                userSelect: 'none',
                transition: 'transform 0.15s',
                '&:active': { transform: 'scale(0.96)' },
              }}
            >
              <Box sx={{ flexShrink: 0, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {screen.icon}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#fff',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }}>
                  {screen.label}
                </Typography>
                <Typography sx={{
                  fontSize: 8,
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}>
                  {screen.subtitle}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );

  // ─── Mobile: All Provider Section ──────────────────────────────────────────

  const gridCols = getGridCols(mobileProviderCategory);

  const mobileAllProviderSection = (
    <Box ref={allProviderRef} sx={{ mb: 2 }}>
      {renderSectionHeader(null, t('home.allprovider') !== 'home.allprovider' ? t('home.allprovider') : 'ALL PROVIDER')}

      <Box sx={{ display: 'flex', gap: 1, px: 1, alignItems: 'flex-start' }}>
        {/* Left: Category Tabs */}
        <Box sx={{
          width: 58,
          flexShrink: 0,
          // uncomment for scroll effect
          // maxHeight: 440,
          // overflowY: 'auto',
          // overflowX: 'visible',
          // scrollbarWidth: 'none',
          // '&::-webkit-scrollbar': { display: 'none' },
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          pb: 1,
          pr: '3px',
        }}>
          {additionalGameBarItems.map((item, idx) => (
            <Box
              key={idx}
              onClick={() => { setMobileProviderCategory(item.code); setProviderSearchQuery(''); }}
              sx={{
                cursor: 'pointer',
                borderRadius: '6px',
                border: mobileProviderCategory === item.code
                  ? `2px solid ${theme.palette.secondary.main}`
                  : '2px solid transparent',
                opacity: mobileProviderCategory === item.code ? 1 : 0.65,
                transition: 'opacity 0.2s, border-color 0.2s, background 0.2s',
                background: mobileProviderCategory === item.code
                  ? `linear-gradient(to bottom, ${lighten(theme.palette.secondary.main, 0.25)}, ${theme.palette.secondary.main})`
                  : 'transparent',
                boxShadow: '1px 1px 0 rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              <Box
                component="img"
                src={mobileProviderCategory === item.code ? (item.imageSelected || item.image) : item.image}
                alt={item.name}
                sx={{ width: '100%', borderRadius: '4px', display: 'block' }}
              />
            </Box>
          ))}
        </Box>

        {/* Right: Search + Grid */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Search bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            background: process.env.REACT_APP_COLOR_SEARCHBAR_BG || 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            px: 1,
            mb: 1,
            height: 32,
          }}>
            <Typography sx={{ color: '#aaa', fontSize: 13, lineHeight: 1, mr: 0.5 }}> <SearchIcon sx={{ color: process.env.REACT_APP_COLOR_PRIMARY || "white" }} /> </Typography>
            <Box
              component="input"
              value={providerSearchQuery}
              onChange={e => setProviderSearchQuery(e.target.value)}
              placeholder="Search..."
              sx={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: process.env.REACT_APP_COLOR_TEXT || 'white',
                fontSize: 12,
                flex: 1,
                padding: '0 4px',
                '&::placeholder': {
                  color: `${process.env.REACT_APP_COLOR_TEXT || '#ffffff'}99`, // ~60% opacity
                },
              }}
            />
            {providerSearchQuery && (
              <Box
                onClick={() => setProviderSearchQuery('')}
                sx={{ cursor: 'pointer', color: '#aaa', fontSize: 13, lineHeight: 1, pl: 0.5 }}
              >
                ✕
              </Box>
            )}
          </Box>

          {/* Game / Provider Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gap: '6px',
          }}>
            {mobileProviderCategory === 'HOT_GAME' && (
              (homePage?.result?.recommendGame || [])
                .filter(g =>
                  !providerSearchQuery ||
                  g.name.toLowerCase().includes(providerSearchQuery.toLowerCase())
                )
                .map((game, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleHotGameClick(game)}
                    sx={{ cursor: 'pointer', minWidth: 0 }}
                  >
                    <Box
                      component="img"
                      src={game.homeThumbnailUrl}
                      alt={Constant.ALT_TAG_GENERAL_GAME + game.name}
                      sx={{ width: '100%', display: 'block', borderRadius: '6px', minWidth: 0 }}
                    />
                    <Typography
                      fontSize={9}
                      fontWeight={400}
                      textAlign="left"
                      sx={{ color: process.env.REACT_APP_COLOR_TEXT || 'white', mt: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {game.name}
                    </Typography>
                  </Box>
                ))
            )}

            {mobileProviderCategory !== 'HOT_GAME' &&
              !isGameProviderFetching && !isGameProviderLoading &&
              (gameProvider?.result || [])
                .filter(p =>
                  !providerSearchQuery ||
                  p.name.toLowerCase().includes(providerSearchQuery.toLowerCase())
                )
                .map((provider, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleProviderClick(provider)}
                    sx={{ cursor: 'pointer', borderRadius: '6px', overflow: 'hidden', minWidth: 0 }}
                  >
                    <Box
                      component="img"
                      src={provider.homeThumbnailUrl}
                      alt={Constant.ALT_TAG_GENERAL_PROVIDER + provider.name}
                      sx={{ width: '100%', display: 'block', borderRadius: '6px', minWidth: 0 }}
                    />
                  </Box>
                ))
            }

            {/* Loading state */}
            {mobileProviderCategory !== 'HOT_GAME' && (isGameProviderFetching || isGameProviderLoading) && (
              <Box sx={{ gridColumn: `1 / -1`, textAlign: 'center', py: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Loading...</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // ───────────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <style>
        {`
        .slick-dots {
          margin-bottom: ${mobileView ? "-80px" : "-110px"};
        }
        .slick-dots li button:before {
          color: grey !important;
          opacity: 1 !important;
        }
        .slick-dots li.slick-active button:before {
          color: white !important;
        }
      `}
      </style>

      {dialog}

      <Box pt={{ xs: 7, sm: 0, lg: 0, xl: 0 }} style={{ background: theme.palette.background.home }}>

        {/* Top Divider */}
        {glowDivider}

        {/* Banner */}
        <ImageCarousel
          banners={
            (mobileView
              ? homePage?.result?.mobileBanners
              : homePage?.result?.banners
            )?.filter(Boolean)
          }
          navigation={false}
          spaceBetween={0}
          alt={Constant.ALT_TAG_BANNER}
        />

        {/* Announcement */}
        {homePage?.result?.announcements && (
          <Announcement announcementDetails={homePage?.result?.announcements} />
        )}

        {glowDivider}

        {/* ── MOBILE LAYOUT ─────────────────────────────────────────────── */}
        {mobileView && !appGameDisplay && (
          <>
            {/* Section B: Quick Action Promo Buttons */}
            {mobilePromoButtons}

            {/* Referral Bar — left label + right panel that pops above (matches judiking refer-top-bar) */}
            {process.env.REACT_APP_FOOTER_REPLACE_AFFILIATE_WITH_REFERRAL === "true" && (
              <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%', mb: 0.5, mt: 0.5, borderTop: `1px solid ${BUTTON_COLORS.Referral}`, borderBottom: `1px solid ${BUTTON_COLORS.Referral}` }}>
                {/* Left piece — background spans full bar width (so the white panel's curved corner has green behind it); text is capped so it can never run under the panel */}
                <Box sx={{
                  background: BUTTON_COLORS.Referral,
                  px: 2, py: '14px',
                  display: 'flex', alignItems: 'center',
                }}>
                  <Box sx={{ maxWidth: '34%', overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 'clamp(9px, 3vw, 13px)', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.15 }}>
                      {t('home.referral') !== 'home.referral' ? t('home.referral') : 'REFERRAL'}
                    </Typography>
                    <Typography sx={{ fontSize: 'clamp(7px, 2vw, 9px)', color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: 0.2, mt: '3px' }}>
                      {t('home.referralsubtitle') !== 'home.referralsubtitle' ? t('home.referralsubtitle') : 'Share to earn more!'}
                    </Typography>
                  </Box>
                </Box>

                {/* Right piece — absolutely positioned, 70% wide, pops above left with shadow + rounded left corners */}
                <Box sx={{
                  position: 'absolute',
                  right: 0, top: 0, bottom: 0,
                  width: '63%',
                  borderTopLeftRadius: '7px',
                  borderBottomLeftRadius: '40px',
                  background: theme.palette.background.paper,
                  boxShadow: '-3px 0 12px 4px rgba(0,0,0,0.45)',
                  display: 'flex',
                  alignItems: 'stretch',
                }}>
                  {[
                    {
                      key: 'copy',
                      icon: <ContentCopyIcon sx={{ fontSize: 19, color: linkCopied ? '#4caf50' : BUTTON_COLORS.Referral }} />,
                      label: linkCopied ? 'Copied!' : (t('home.copylink') !== 'home.copylink' ? t('home.copylink') : 'Copy Link'),
                      color: linkCopied ? '#4caf50' : BUTTON_COLORS.Referral,
                      onClick: handleCopyReferralLink,
                    },
                    {
                      key: 'downline',
                      icon: <PeopleAltIcon sx={{ fontSize: 19, color: BUTTON_COLORS.Referral }} />,
                      label: t('home.downline') !== 'home.downline' ? t('home.downline') : 'Downline',
                      color: BUTTON_COLORS.Referral,
                      onClick: () => {
                        if (!isLoggedIn) { navigate({ pathname: location.pathname, search: "?login=1" }); return; }
                        navigate(Constant.getPath("/referral"), { state: { initialTab: 3 } });
                      },
                    },
                    {
                      key: 'info',
                      icon: <InfoOutlinedIcon sx={{ fontSize: 19, color: BUTTON_COLORS.Referral }} />,
                      label: t('home.moreinfo') !== 'home.moreinfo' ? t('home.moreinfo') : 'More Info',
                      color: BUTTON_COLORS.Referral,
                      onClick: () => {
                        if (!isLoggedIn) { navigate({ pathname: location.pathname, search: "?login=1" }); return; }
                        navigate(Constant.getPath("/referral"));
                      },
                    },
                  ].map((btn, i, arr) => (
                    <Box key={btn.key} onClick={btn.onClick} sx={{
                      flex: 1,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', py: 1, gap: 0.5,
                      borderRight: i < arr.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    }}>
                      {btn.icon}
                      <Typography sx={{ fontSize: 8, fontWeight: 500, color: btn.color, textTransform: 'uppercase', whiteSpace: 'nowrap', letterSpacing: 0.2 }}>
                        {btn.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* {glowDivider} */}

            {/* Section C: HOT GAMES Horizontal Scroller */}
            {/* {homePage?.result?.recommendGame?.length > 0 && (
              <Box >
                {renderSectionHeaderFeatured(
                  process.env.REACT_APP_HEADER_LOGO + '/top_icon.png',
                  t('home.hotgames') !== 'home.hotgames' ? t('home.hotgames') : 'HOT GAMES'
                )}
                {renderHorizontalGameScroller(homePage.result.recommendGame, handleHotGameClick)}
              </Box>
            )} */}

            {/* Section D: Featured game categories from backend (homePage.result.featureGames)
                Only renders for categories that have is_feature_game=true games.
                HOT_GAME excluded — it uses recommendGame (Section C above). */}
            {additionalGameBarItems
              .filter(item => item.code !== 'HOT_GAME')
              .filter(item => (featureGamesByCategory[item.code]?.length ?? 0) > 0)
              .map(item => (
                <Box key={item.code}>
                  {renderSectionHeaderFeatured(process.env.REACT_APP_HEADER_LOGO + '/top_icon.png', item.name)}
                  {renderHorizontalGameScroller(featureGamesByCategory[item.code], handleHotGameClick)}
                </Box>
              ))
            }

            {/* Section E: All Provider */}
            {mobileAllProviderSection}
          </>
        )}

        {/* App Game PopUp */}
        {(isLaunchGameLoading || isLaunchGameFetching) && <Backdrop />}
        <Container style={{ maxWidth: '3000px' }}>
          {!isLaunchGameLoading && !isLaunchGameFetching && isLaunchGameSuccess && (
            <>
              {appGameDisplay && (
                <Box display="flex" flexDirection="column" pt={2}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignSelf="center"
                    pt={2}
                  >
                    <Image src={launchGame?.result?.bannerUrl} />
                    <Typography color={theme.palette.primary.main} pt={2}>
                      {`EASY STEP BY STEP TO PLAY ${appProviderName} ON YOUR MOBILE`}
                    </Typography>
                    <Typography color={theme.palette.primary.main} pt={2}>Step 1</Typography>
                    <Typography color={process.env.REACT_APP_COLOR_APPGAME_TEXT || "black"}>
                      {`Transfer fund into ${appProviderName} wallet to get your login ID and password on the below section of this page.`}
                    </Typography>
                    <Typography color={theme.palette.primary.main} pt={2}>Step 2</Typography>
                    <Typography color={process.env.REACT_APP_COLOR_APPGAME_TEXT || "black"}>
                      {`Download ${appProviderName} from the download link below.`}
                    </Typography>
                    <Typography color={theme.palette.primary.main} pt={2}>Step 3</Typography>
                    <Typography color={process.env.REACT_APP_COLOR_APPGAME_TEXT || "black"} pb={3}>
                      {`Launch the apps in your mobile and login with the ID and password from below.`}
                    </Typography>
                    <Divider />
                    <FormControl sx={{ display: "flex", flexDirection: "column", pt: 4, "& .MuiTextField-root": { pb: 3 } }}>
                      <TextField
                        label="Username"
                        value={launchGame?.result?.mobileAppUsername}
                        InputLabelProps={{ shrink: true }}
                        disabled
                        type="text"
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton disableRipple sx={{ "&.MuiButtonBase-root:hover": { backgroundColor: "transparent" } }}>
                                <CopiedTooltip text={launchGame?.result?.mobileAppUsername} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Password"
                        value={launchGame?.result?.mobileAppPassword}
                        InputLabelProps={{ shrink: true }}
                        disabled
                        type="text"
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton disableRipple sx={{ "&.MuiButtonBase-root:hover": { backgroundColor: "transparent" } }}>
                                <CopiedTooltip text={launchGame?.result?.mobileAppPassword} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box display="flex" alignSelf="center">
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: theme.palette.button.primary.main }}
                      onClick={() => {
                        let url = launchGame.result.mobileAppUrlIos[0];
                        if (!pattern.test(url)) url = "https://" + url;
                        window.open(url, "_blank");
                      }}
                    >
                      IOS
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: theme.palette.button.primary.main, ml: 2 }}
                      onClick={() => {
                        let url = launchGame.result.mobileAppUrlAndroid[0];
                        if (!pattern.test(url)) url = "https://" + url;
                        window.open(url, "_blank");
                      }}
                    >
                      ANDROID
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Container>

        {/* ── DESKTOP LAYOUT (UNCHANGED) ────────────────────────────────── */}

        {/* Desktop: Provider Icon Scroll Bar */}
        {!mobileView && (
          <Grid container spacing={3} sx={{
            margin: "0 auto",
            marginTop: "15px",
            marginBottom: "25px",
            overflowX: "scroll",
            width: "90%",
            flexWrap: "nowrap",
            textAlign: "center",
            '&::-webkit-scrollbar': { display: "none" }
          }}>
            <Grid key={-1} item>
              <Box
                sx={{
                  border: 1,
                  display: "flex",
                  justifyContent: "center",
                  height: "50px",
                  width: "50px",
                  alignItems: "center",
                  boxSizing: "border-box",
                  wordWrap: "break-word",
                  cursor: "pointer",
                  borderRadius: "50%",
                  borderColor: currentProviderId === null
                    ? theme.palette.primary.main
                    : darken(theme.palette.background.paper, 0.2),
                  overflow: "hidden",
                }}
                onClick={() => setCurrentProviderId(null)}
              >
                <Typography color={process.env.REACT_APP_COLOR_TEXT || "white"} fontWeight={600}>ALL</Typography>
              </Box>
              <Typography
                color={currentProviderId === null ? theme.palette.tertiary.main : process.env.REACT_APP_COLOR_TEXT || "white"}
                fontSize="12px"
                pt="8px"
              >
                {"ALL"}
              </Typography>
            </Grid>

            {gameProvider?.result?.map((provider, index) => (
              <Grid key={index} item sx={{ pl: "15px !important" }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '50px',
                    height: '50px',
                    margin: '0 auto',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCurrentProviderId(provider.id)}
                >
                  {provider.isHotProvider && (
                    <img
                      src={"/assets/img/hotgif.gif"}
                      alt="Hot Provider"
                      style={{ position: 'absolute', top: '-10px', left: '-5px', width: '25px', height: '25px', zIndex: 2 }}
                    />
                  )}
                  <Box
                    sx={{
                      border: 1,
                      display: "flex",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      alignItems: "center",
                      boxSizing: "border-box",
                      borderRadius: "50%",
                      borderColor: currentProviderId === provider.id
                        ? theme.palette.primary.main
                        : darken(theme.palette.background.paper, 0.2),
                      overflow: "hidden",
                      position: 'relative',
                      zIndex: 1,
                      backgroundColor: currentProviderId === provider.id
                        ? theme.palette.primary.main
                        : "#313338",
                    }}
                  >
                    <img
                      src={currentProviderId === provider.id ? provider.logoSelectedUrl : provider.logoUrl}
                      alt={Constant.ALT_TAG_GENERAL_PROVIDER + provider.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Box>
                <Typography
                  color={currentProviderId === provider.id ? theme.palette.tertiary.main : process.env.REACT_APP_COLOR_TEXT || "white"}
                  fontSize="12px"
                  pt="8px"
                >
                  {provider.name.split(" ")[0].length > 6
                    ? provider.name.split(" ")[0].slice(0, 6) + "..."
                    : provider.name.split(" ")[0]}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Desktop: Game Item List */}
        {!mobileView && (
          <Box pb={5}>
            {!isGameItemLoading && isGameItemSuccess && (
              <>
                <Grid
                  container
                  spacing={1}
                  justifyContent="center"
                  mt={1}
                  width="90%"
                  margin="0 auto"
                >
                  {gameItem?.result?.data?.map((game, index) => (
                    <>
                      {!appGameDisplay && (
                        <Grid
                          item
                          xs={3}
                          sm={2}
                          md={1.5}
                          key={index}
                          display="flex"
                          justifyContent="center"
                          sx={{ position: "relative", mb: 1 }}
                        >
                          <GameCard
                            imageUrl={game?.thumbnailUrl}
                            isHotGame={game.isHotGame}
                            imageWidth={tabletView ? "120px" : "150px"}
                            imageHeight={tabletView ? "120px" : "150px"}
                            height="150px"
                            alt={Constant.ALT_TAG_GENERAL_GAME + game.name}
                            onClick={() => {
                              if (!isLoggedIn) {
                                if (game?.demoSupported) {
                                  playDemo(game);
                                } else {
                                  navigate({ pathname: location.pathname, search: "?login=1" });
                                }
                              } else {
                                setAppProviderName(
                                  gameProvider?.result?.find(p => p?.id === game?.providerId)?.name
                                );
                                getLaunchGame({ id: game?.id, type: game?.gameType, isMobile: mobileView });
                              }
                            }}
                            title={game?.name}
                            subtitle={gameProvider?.result?.find(p => p?.id === game?.providerId)?.name || ""}
                            description={null}
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>

                {gameItem?.result?.totalPage !== 0 && !appGameDisplay && (
                  <Stack mt={tabletView ? 0 : 2} spacing={2} size="small" alignItems="center">
                    <Pagination
                      onChange={handlePageChange}
                      count={gameItem?.result?.totalPage}
                      shape="rounded"
                      page={offset}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
                          "&.Mui-selected": {
                            background: lighten(
                              process.env.REACT_APP_COLOR_PAGINATION || theme.palette.background.paper,
                              0.02
                            ),
                            color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER1 || "white",
                          },
                        },
                        "& .MuiPaginationItem-icon": {
                          color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
                        },
                      }}
                    />
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}

        {/* Live Matches — Mobile */}
        {mobileView && liveMatches?.result?.length > 0 && (
          <>
            <Grid
              container
              spacing={3}
              sx={{ marginTop: '15px', marginBottom: '35px', textAlign: 'center', display: 'flex', flexDirection: 'column' }}
            >
              <Grid display={'flex'} flexDirection={'row'} alignItems={'center'} key={-1} item>
                <Box display={'flex'} flexDirection={'row'} paddingLeft={'20px'}>
                  <Typography fontSize={'12px'} fontWeight={'bold'} color='white'>
                    {t("home.livematches")}
                  </Typography>
                  <Grid color='white' gap={1} marginLeft={'25px'} display={'flex'} flexDirection={'row'}>
                    {[...new Set(liveMatches.result.map((match) => {
                      const category = JSON.parse(match.category);
                      const val = category[lang.toLocaleUpperCase()] || category['EN'];
                      return typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? "");
                    }))].map((category) => (
                      <Grid
                        item
                        key={category}
                        fontSize={'11px'}
                        sx={{
                          cursor: 'pointer',
                          padding: '5px 10px',
                          borderRadius: '5px',
                          backgroundColor: selectedCategory === category ? 'red' : 'transparent',
                        }}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              <Grid
                item
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingTop: '5px !important',
                  position: 'relative',
                }}
              >
                <img
                  src={process.env.REACT_APP_LIVE_MATCHES_MOBILE}
                  alt='Live Preview'
                  style={{ width: '100%', height: '230px' }}
                />
                {filteredLiveMatches && filteredLiveMatches.length > 1 ? (
                  <>
                    <Box display="flex" justifyContent="center" position="absolute" top="2%">
                      <Image width={115} height={65} src={filteredLiveMatches?.[activeSlide ?? 0]?.league} />
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: '-3%', width: '85%' }}>
                      <Slider ref={sliderRef} {...slickSettings}>
                        {filteredLiveMatches.map((match, i) => (
                          <Box key={i}>{liveMatchesImages(match)}</Box>
                        ))}
                      </Slider>
                      {liveMatchesDisplay}
                    </Box>
                  </>
                ) : (
                  filteredLiveMatches.map((match, i) => (
                    <React.Fragment key={i}>
                      <Box display="flex" justifyContent="center" position="absolute" top="2%">
                        <Image width={115} height={65} src={filteredLiveMatches?.[activeSlide ?? 0]?.league} />
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: '-3%', width: '85%' }}>
                        {liveMatchesImages(match)}
                        {liveMatchesDisplay}
                      </Box>
                    </React.Fragment>
                  ))
                )}
              </Grid>
            </Grid>
            <br />
          </>
        )}

        {/* Live Matches — Desktop */}
        {!mobileView && liveMatches?.result?.length > 0 && (
          <Grid container spacing={3} sx={{ marginTop: "15px", marginBottom: "60px", textAlign: "center", display: "flex", flexDirection: "column" }}>
            <Grid display={"flex"} flexDirection={"row"} alignItems={"center"} key={-1} item>
              <Box display={"flex"} flexDirection={"row"} paddingLeft="100px">
                <Typography fontSize="14px" fontWeight={"bold"} color="white">
                  {t("home.livematches")}
                </Typography>
                <Grid color="white" gap={2} marginLeft="70px" display={"flex"} flexDirection={"row"}>
                  {[...new Set(liveMatches.result.map(match => {
                    const category = JSON.parse(match.category);
                    const val = category[lang.toLocaleUpperCase()] || category['EN'];
                    return typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? "");
                  }))].map((category) => (
                    <Grid
                      item
                      key={category}
                      fontSize="14px"
                      sx={{ cursor: "pointer", padding: "5px 10px", borderRadius: "5px", backgroundColor: selectedCategory === category ? "red" : "transparent" }}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            <Grid item sx={{ width: "100%", borderRadius: 4, display: "flex", flexDirection: "column", alignItems: "center", paddingLeft: "0 !important", paddingTop: "5px !important", position: "relative" }}>
              <img src={process.env.REACT_APP_LIVE_MATCHES} alt="Live Preview" style={{ width: '100%', height: 'auto' }} />
              {filteredLiveMatches && filteredLiveMatches.length > 1 ? (
                <>
                  <Box display="flex" justifyContent="center" position="absolute" top="0%" sx={{ width: '50%' }}>
                    <Image width={250} height={145} src={filteredLiveMatches?.[activeSlide ?? 0]?.league} />
                  </Box>
                  <Box position="absolute" bottom="6%" sx={{ width: '50%' }}>
                    <Slider ref={sliderRef} {...slickSettings}>
                      {filteredLiveMatches.map((match, i) => (
                        <Box key={i}>{liveMatchesImages(match)}</Box>
                      ))}
                    </Slider>
                    {liveMatchesDisplay}
                  </Box>
                </>
              ) : filteredLiveMatches.map((match, i) => (
                <React.Fragment key={i}>
                  <Box display="flex" justifyContent="center" position="absolute" top="0%" sx={{ width: '50%' }}>
                    <Image width={250} height={145} src={filteredLiveMatches?.[activeSlide ?? 0]?.league} />
                  </Box>
                  <Box sx={{ position: "absolute", bottom: "6%", width: '50%' }}>
                    {liveMatchesImages(match)}
                    {liveMatchesDisplay}
                  </Box>
                </React.Fragment>
              ))}
            </Grid>
          </Grid>
        )}

        {/* Twitch Embed (optional) */}
        {process.env.REACT_APP_SHOW_TWITCH && (
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <iframe
              width={mobileView ? "100%" : "700"}
              height={mobileView ? "250" : "400"}
              src={`https://player.twitch.tv/?channel=marbula2official&autoplay=false&muted=false&parent=${document.location.hostname}`}
              title="Twitch video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </Box>
        )}

        {/* Mobile: Live Preview / Ambassador / Video */}
        {mobileView && (
          <Grid container>
            {process.env.REACT_APP_SHOW_BAGH8_LIVE_PREVIEW === 'true' && (
              <Grid item xs={12} sx={{ position: 'relative' }}>
                <img src={process.env.REACT_APP_LIVE_PREV_MOBILE} alt="Live Preview" style={{ width: '100%', height: 'auto', display: 'block' }} />
                <Typography sx={{ position: 'absolute', top: '15%', left: '5%', transform: 'translateY(-50%)', color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                  {t("home.previewfirstdesc")}
                </Typography>
                <Typography sx={{ position: 'absolute', top: '22%', left: '5%', transform: 'translateY(-50%)', color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                  {t("home.previewseconddesc")}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', top: '40%', left: '5%', transform: 'translateY(-50%)' }}>
                  <img src={process.env.REACT_APP_LOGO} alt="" style={{ width: '70px', height: '30px', borderRadius: '5px', marginRight: '10px' }} />
                  <div style={{ borderLeft: '1px solid white', height: '30px' }} />
                  <img src={process.env.REACT_APP_ICON_PARTNER} alt="" style={{ width: '40px', height: '40px', borderRadius: '5px' }} />
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ position: 'absolute', top: '65%', left: '5%', transform: 'translateY(-50%)', width: '80px', height: '30px', fontWeight: 'bold', fontSize: '10px' }}
                  onClick={() => navigate(Constant.getPath("/game/sportbook"))}
                >
                  {t("home.previewbutton")}
                </Button>
              </Grid>
            )}

            {process.env.REACT_APP_SHOW_BAGH8_AMBASSADOR === 'true' && (
              <Grid item xs={12} sx={{ width: '100%', textAlign: 'center' }}>
                <img src={process.env.REACT_APP_INFLUENCER} alt="Influencer" style={{ maxWidth: '100%', height: 'auto' }} />
              </Grid>
            )}

            {process.env.REACT_APP_SHOW_BAGH8_VIDEO === 'true' && (
              <Grid container direction="column" alignItems="center" sx={{ padding: 2 }}>
                <Typography sx={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                  {t("home.videotitle")}
                </Typography>
                <iframe
                  width="100%"
                  height="350"
                  src="https://www.youtube.com/embed/8rpbFfOidqg?si=W447qF6EAQvnOLxa"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Desktop: Live Preview / Ambassador / Video */}
        {!mobileView && (
          <Grid container>
            {process.env.REACT_APP_SHOW_BAGH8_LIVE_PREVIEW === 'true' && (
              <Grid item xs={12} sx={{ position: 'relative' }}>
                <img src={process.env.REACT_APP_LIVE_PREV} alt="Live Preview" style={{ width: '100%', height: 'auto', display: 'block' }} />
                <Typography sx={{ position: 'absolute', top: '20%', left: '15%', transform: 'translateY(-50%)', color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                  {t("home.previewfirstdesc")}
                </Typography>
                <Typography sx={{ position: 'absolute', top: '25%', left: '15%', transform: 'translateY(-50%)', color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                  {t("home.previewseconddesc")}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', top: '35%', left: '15%', transform: 'translateY(-50%)' }}>
                  <img src={process.env.REACT_APP_LOGO} alt="" style={{ width: '100px', height: '40px', borderRadius: '5px', marginRight: '10px' }} />
                  <div style={{ borderLeft: '1px solid white', height: '60px', margin: '0 10px' }} />
                  <img src={process.env.REACT_APP_ICON_PARTNER} alt="" style={{ width: '60px', height: '60px', borderRadius: '5px' }} />
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ position: 'absolute', top: '50%', left: '15%', transform: 'translateY(-50%)', fontWeight: 'bold' }}
                  onClick={() => navigate(Constant.getPath("/game/sportbook"))}
                >
                  {t("home.previewbutton")}
                </Button>
              </Grid>
            )}

            {process.env.REACT_APP_SHOW_BAGH8_AMBASSADOR === 'true' && (
              <Grid item xs={12} sx={{ width: '100%', textAlign: 'center' }}>
                <img src={process.env.REACT_APP_INFLUENCER} alt="Influencer" style={{ maxWidth: '100%', height: 'auto' }} />
              </Grid>
            )}

            {process.env.REACT_APP_SHOW_BAGH8_VIDEO === 'true' && (
              <Grid container direction="column" alignItems="center" sx={{ padding: 2 }}>
                <Typography sx={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                  {t("home.videotitle")}
                </Typography>
                <iframe
                  width="700"
                  height="500"
                  src="https://www.youtube.com/embed/8rpbFfOidqg?si=W447qF6EAQvnOLxa"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Mobile: Feature Banner (wallet steps) — hidden per request */}
        {/*
        {mobileView && (
          <Box >
            <Container>
              <Box pb={11}>
                <Image src={homePage?.result?.walletSteps} alt={Constant.ALT_TAG_STEPS} />
              </Box>
            </Container>
          </Box>
        )}
        */}

        {/* Desktop: SEO Text */}
        {!mobileView && homePageSeo && !useSEO && (
          <Box sx={{ backgroundColor: theme.palette.background.home }}>
            <Container>
              <Box pt={{ xs: 4, sm: 4, md: 4 }} pb={{ xs: 11, sm: 5, md: 5 }}>
                <TextCarousel html={[homePageSeo]} slidesPerView={1} pagination={true} spaceBetween={0} />
              </Box>
            </Container>
          </Box>
        )}

        {/* Login Reward & Popup Banner */}
        {showLoginReward && (
          <LoginReward loginRewardOpen={showLoginReward} setLoginRewardOpen={setShowLoginReward} />
        )}
        {popupOpen && popupBanner?.result?.length > 0 &&
          !isPopupBannerFetching && !isPopupBannerLoading && isPopupBannerSuccess &&
          searchParams.get("register") !== "1" && searchParams.get("login") !== "1" && (
            <PopupBanner data={popupBanner} />
          )}

      </Box>
    </>
  );
};

export default Home_v2;
